/**
 * f12.1 (Q-f12.1-3): the unit chips with "Add unit" (units grouped by Business
 * Unit Group, one-click whole-group add) and the H / M / L risk filter.
 * The selection lives in the URL, so a meeting link is bookmarkable.
 */

import { useState } from 'react';
import { Plus, X } from 'lucide-react';

import type { ReviewUnits } from '@/api/client';

export const RISK_OPTIONS = ['', 'H', 'M', 'L'] as const;

export function ReviewUnitFilter({
  units,
  selected,
  onChange,
  risk,
  onRisk,
}: {
  units: ReviewUnits;
  selected: readonly string[];
  onChange: (next: string[]) => void;
  risk: string;
  onRisk: (risk: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const all = units.groups.flatMap((g) => g.units);
  const name = (id: string) => all.find((u) => u.id === id)?.name ?? id;
  const remaining = (ids: readonly string[]) => ids.filter((id) => !selected.includes(id));

  return (
    <div className="toolbar rv-toolbar">
      <div className="row-wrap rv-chips">
        {selected.map((id) => (
          <span key={id} className="badge badge-primary rv-chip">
            {name(id)}
            <button
              type="button"
              className="rv-chip-x"
              aria-label={`Remove ${name(id)}`}
              disabled={selected.length === 1}
              onClick={() => onChange(selected.filter((s) => s !== id))}
            >
              <X aria-hidden="true" />
            </button>
          </span>
        ))}
        <div className="rv-add">
          <button type="button" className="btn btn-secondary btn-xs" aria-expanded={open} onClick={() => setOpen(!open)}>
            <Plus aria-hidden="true" /> Add unit
          </button>
          {open && (
            <div className="rv-menu" role="menu">
              {units.groups.map((group) => {
                const addable = remaining(group.units.map((u) => u.id));
                return (
                  <div key={group.id} className="rv-menu-group">
                    <div className="rv-menu-head">
                      <span>{group.name}</span>
                      <button
                        type="button"
                        className="btn btn-secondary btn-xs"
                        disabled={addable.length === 0}
                        onClick={() => {
                          onChange([...selected, ...addable]);
                          setOpen(false);
                        }}
                      >
                        Whole group
                      </button>
                    </div>
                    {group.units.map((unit) => (
                      <button
                        key={unit.id}
                        type="button"
                        role="menuitem"
                        className="rv-menu-item"
                        disabled={selected.includes(unit.id)}
                        onClick={() => {
                          onChange([...selected, unit.id]);
                          setOpen(false);
                        }}
                      >
                        {unit.name} <span className="mono">{unit.reporting_currency_code}</span>
                      </button>
                    ))}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
      <div className="spacer" />
      <div className="rv-segmented" role="radiogroup" aria-label="Risk filter">
        {RISK_OPTIONS.map((code) => (
          <button
            key={code || 'all'}
            type="button"
            role="radio"
            aria-checked={risk === code}
            className={`rv-seg${code ? ` rv-seg-${code}` : ''}${risk === code ? ' is-on' : ''}`}
            onClick={() => onRisk(code)}
          >
            {code || 'All'}
          </button>
        ))}
      </div>
    </div>
  );
}
